import { useEffect, useRef, useState, memo } from 'react';
import mermaid from 'mermaid';

interface MermaidProps {
  chart: string;
}

/**
 * Mermaid図表をレンダリングするコンポーネント
 */
const MermaidComponent: React.FC<MermaidProps> = ({ chart }) => {
  const mermaidRef = useRef<HTMLDivElement>(null);
  const modalMermaidRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scale, setScale] = useState(1.0);
  const [initialScale, setInitialScale] = useState(1.0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isPinching, setIsPinching] = useState(false);
  const [pinchStartDistance, setPinchStartDistance] = useState(0);
  const [pinchStartScale, setPinchStartScale] = useState(1.0);
  const [showPinchHint, setShowPinchHint] = useState(false);
  const idRef = useRef<string | null>(null);
  const modalIdRef = useRef<string | null>(null);
  const hasRenderedRef = useRef<boolean>(false);
  
  // IDを一度だけ生成（再レンダリング時に再生成されないように）
  if (!idRef.current) {
    idRef.current = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
  }
  if (!modalIdRef.current) {
    modalIdRef.current = `mermaid-modal-${Math.random().toString(36).substr(2, 9)}`;
  }

  const renderMermaid = async (element: HTMLDivElement, id: string) => {
    if (!element || !chart.trim()) {
      return;
    }

    // 既に同じchartでレンダリング済みの場合はスキップ
    const existingChart = element.getAttribute('data-chart');
    const existingId = element.getAttribute('data-mermaid-id');
    const hasSvg = element.querySelector('svg') !== null;
    const svgGElement = element.querySelector('svg g');
    const svgHasContent = svgGElement?.children && svgGElement.children.length > 0;
    
    // 同じchartで、SVGが存在し、コンテンツがある場合はスキップ
    if (existingChart === chart && hasSvg && svgHasContent && existingId === id) {
      return Promise.resolve();
    }
    
    // レンダリング中の場合はスキップ（同じIDで複数回レンダリングを防ぐ）
    if (element.getAttribute('data-rendering') === 'true') {
      return Promise.resolve();
    }
    
    // レンダリング中フラグを設定
    element.setAttribute('data-rendering', 'true');

    // Mermaidの初期化（初回のみ）
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'loose',
    });

    // 既存のコンテンツをクリア
    element.innerHTML = '';
    element.setAttribute('data-processed', 'false');

    // Mermaid図表をレンダリング
    try {
      const result = await mermaid.render(id, chart);
      
      // SVGが空の場合はエラーとして扱う（基本的なチェックのみ）
      if (!result.svg || result.svg.trim() === '') {
        throw new Error('Mermaid rendered empty SVG. The diagram syntax may be invalid.');
      }
      
      // 空の<g>要素のチェックは削除
      // Mermaidの正常な出力には空の<g class="clusters"></g>などが含まれることがあるため、
      // これらのチェックは誤検出を引き起こす可能性がある
      // Mermaidが正常にレンダリングした場合、mermaid.render()は有効なSVGを返すため、
      // 追加の検証は不要
      
      element.innerHTML = result.svg;
      element.setAttribute('data-chart', chart);
      element.setAttribute('data-mermaid-id', id);
      element.setAttribute('data-processed', 'true');
      element.removeAttribute('data-rendering');
      setError(null);
      return Promise.resolve();
    } catch (err) {
      console.error('Mermaid rendering error:', err);
      
      // エラーメッセージをより分かりやすくする
      let errorMessage = 'Mermaid図表のレンダリングに失敗しました';
      if (err instanceof Error) {
        // パースエラーの場合、より詳細な情報を提供
        if (err.message.includes('Parse error')) {
          errorMessage = `Mermaid図表の構文エラー: ${err.message}\n\nヒント: 日本語のコメントを使用する場合は、行コメント（--）を使用してください。`;
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      element.innerHTML = '';
      element.removeAttribute('data-rendering');
      return Promise.reject(err);
    }
  };

  useEffect(() => {
    // 既にレンダリング済みで、chartが変更されていない場合は再レンダリングをスキップ
    if (mermaidRef.current) {
      const existingChart = mermaidRef.current.getAttribute('data-chart');
      const hasSvg = mermaidRef.current.querySelector('svg') !== null;
      
      // chartが変更されていない、かつSVGが既に存在する場合はスキップ
      if (existingChart === chart && hasSvg) {
        return;
      }
      
      renderMermaid(mermaidRef.current, idRef.current!).then(() => {
        hasRenderedRef.current = true;
      }).catch((err) => {
        // エラーは既にsetErrorで処理されているため、ここでは何もしない
        console.error('Mermaid rendering failed:', err);
      });
    }
  }, [chart]);

  useEffect(() => {
    if (isModalOpen && modalMermaidRef.current && modalIdRef.current) {
      renderMermaid(modalMermaidRef.current, modalIdRef.current).then(() => {
        // 図表を大きく表示するために、SVGのサイズを調整
        setTimeout(() => {
          const svg = modalMermaidRef.current?.querySelector('svg');
          if (svg) {
            // viewBoxを取得
            const viewBox = svg.viewBox?.baseVal;
            if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
              // コンテナの幅を取得（パディングを考慮）
              const container = svg.closest('.mermaid-modal-content');
              if (container) {
                const containerWidth = container.clientWidth - 80; // パディング40px x 2
                const containerHeight = window.innerHeight * 0.8; // 画面の80%
                
                // アスペクト比を維持しながら、コンテナに合わせて拡大
                const scaleX = containerWidth / viewBox.width;
                const scaleY = containerHeight / viewBox.height;
                const calculatedScale = Math.min(scaleX, scaleY);
                
                // 初期スケールを設定（1.0を基準とする）
                setInitialScale(calculatedScale);
                setScale(1.0);
                
                // SVGのサイズを設定（初期スケールで）
                svg.setAttribute('width', `${viewBox.width * calculatedScale}`);
                svg.setAttribute('height', `${viewBox.height * calculatedScale}`);
                // CSSのmax-width/max-height制限を解除して拡大を可能にする
                svg.style.maxWidth = 'none';
                svg.style.maxHeight = 'none';
              }
            }
          }
        }, 100);
      });
    }
  }, [isModalOpen, chart]);

  const handleClick = () => {
    setIsModalOpen(true);
    setScale(1.0); // モーダルを開くときにスケールをリセット
    setPosition({ x: 0, y: 0 }); // 位置もリセット
    // スマホではピンチイン・アウトのヒントを表示
    if (window.innerWidth <= 768) {
      setShowPinchHint(true);
      // 3秒後に自動的に非表示
      setTimeout(() => {
        setShowPinchHint(false);
      }, 3000);
    }
  };

  const handleCloseModal = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsModalOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsModalOpen(false);
    }
  };

  // 拡大機能（5段階: 1.25, 1.5, 1.75, 2.0, 2.5倍）
  const handleZoomIn = () => {
    const zoomLevels = [1.0, 1.25, 1.5, 1.75, 2.0, 2.5];
    const currentIndex = zoomLevels.findIndex(level => Math.abs(level - scale) < 0.01);
    if (currentIndex < zoomLevels.length - 1) {
      setScale(zoomLevels[currentIndex + 1]);
    }
  };

  // 縮小機能
  const handleZoomOut = () => {
    const zoomLevels = [1.0, 1.25, 1.5, 1.75, 2.0, 2.5];
    const currentIndex = zoomLevels.findIndex(level => Math.abs(level - scale) < 0.01);
    if (currentIndex > 0) {
      setScale(zoomLevels[currentIndex - 1]);
    }
  };

  // リセット機能
  const handleZoomReset = () => {
    setScale(1.0);
    setPosition({ x: 0, y: 0 });
  };

  // ドラッグ機能
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // 左クリックのみ
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // グローバルマウスイベント
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  // 2点間の距離を計算
  const getDistance = (touch1: Touch, touch2: Touch): number => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // ピンチジェスチャーの開始
  const handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      setIsPinching(true);
      setIsDragging(false);
      const distance = getDistance(e.touches[0], e.touches[1]);
      setPinchStartDistance(distance);
      setPinchStartScale(scale);
    } else if (e.touches.length === 1) {
      // 1本指の場合はドラッグとして処理
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  };

  // ピンチジェスチャーの更新
  const handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 2 && isPinching) {
      e.preventDefault();
      const distance = getDistance(e.touches[0], e.touches[1]);
      const scaleRatio = distance / pinchStartDistance;
      const newScale = Math.max(1.0, Math.min(2.5, pinchStartScale * scaleRatio));
      
      // ズームレベルに最も近い値に丸める
      const zoomLevels = [1.0, 1.25, 1.5, 1.75, 2.0, 2.5];
      const closestLevel = zoomLevels.reduce((prev, curr) => 
        Math.abs(curr - newScale) < Math.abs(prev - newScale) ? curr : prev
      );
      setScale(closestLevel);
    } else if (e.touches.length === 1 && isDragging) {
      e.preventDefault();
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y,
      });
    }
  };

  // ピンチジェスチャーの終了
  const handleTouchEnd = (e: TouchEvent) => {
    if (e.touches.length < 2) {
      setIsPinching(false);
    }
    if (e.touches.length === 0) {
      setIsDragging(false);
    }
  };

  // タッチイベントリスナーの登録（passive: falseでpreventDefaultを有効化）
  useEffect(() => {
    if (!isModalOpen) return;

    const diagramElement = modalMermaidRef.current?.parentElement;
    if (!diagramElement) return;

    diagramElement.addEventListener('touchstart', handleTouchStart, { passive: false });
    diagramElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    diagramElement.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      diagramElement.removeEventListener('touchstart', handleTouchStart);
      diagramElement.removeEventListener('touchmove', handleTouchMove);
      diagramElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isModalOpen, isPinching, isDragging, pinchStartDistance, pinchStartScale, scale, dragStart, position]);

  // スケールに応じてSVGのサイズを更新
  useEffect(() => {
    if (isModalOpen && modalMermaidRef.current && initialScale > 0) {
      const svg = modalMermaidRef.current.querySelector('svg');
      if (svg) {
        const viewBox = svg.viewBox?.baseVal;
        if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
          const newWidth = viewBox.width * initialScale * scale;
          const newHeight = viewBox.height * initialScale * scale;
          svg.setAttribute('width', `${newWidth}`);
          svg.setAttribute('height', `${newHeight}`);
          // CSSのmax-width/max-height制限を解除して拡大を可能にする
          svg.style.maxWidth = 'none';
          svg.style.maxHeight = 'none';
        }
      }
    }
  }, [scale, initialScale, isModalOpen]);

  if (error) {
    // エラーメッセージを行ごとに分割して表示
    const errorLines = error.split('\n');
    const title = errorLines[0];
    const details = errorLines.slice(1).join('\n');
    
    return (
      <div className="mermaid-container">
        <div className="mermaid-error">
          <p>{title}</p>
          {details && <pre>{details}</pre>}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mermaid-container mermaid-clickable" onClick={handleClick} title="クリックで拡大表示">
        <div 
          ref={(el) => {
            mermaidRef.current = el;
          }} 
          className="mermaid" 
        />
        <div className="mermaid-overlay">
          <span className="mermaid-expand-icon">🔍</span>
        </div>
      </div>
      {isModalOpen && (
        <div
          className="mermaid-modal"
          onClick={handleCloseModal}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          <div className="mermaid-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="mermaid-modal-close"
              onClick={() => setIsModalOpen(false)}
              aria-label="閉じる"
            >
              ×
            </button>
            <div className="mermaid-modal-controls mermaid-modal-controls--desktop">
              <button
                className="mermaid-control-btn"
                onClick={handleZoomIn}
                disabled={scale >= 2.5}
                title="拡大 (+)"
                aria-label="拡大"
              >
                +
              </button>
              <button
                className="mermaid-control-btn"
                onClick={handleZoomOut}
                disabled={scale <= 1.0}
                title="縮小 (-)"
                aria-label="縮小"
              >
                −
              </button>
              <button
                className="mermaid-control-btn"
                onClick={handleZoomReset}
                disabled={scale === 1.0}
                title="リセット"
                aria-label="リセット"
              >
                ↻
              </button>
              <span className="mermaid-zoom-info">{Math.round(scale * 100)}%</span>
            </div>
            {showPinchHint && (
              <div className="mermaid-pinch-hint">
                <div className="mermaid-pinch-hint-icon">🤏</div>
                <div className="mermaid-pinch-hint-text">ピンチイン・アウトで拡大・縮小できます</div>
              </div>
            )}
            <div
              className="mermaid-modal-diagram"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
            >
              <div
                ref={modalMermaidRef}
                style={{
                  transform: `translate(${position.x}px, ${position.y}px)`,
                  transition: (isDragging || isPinching) ? 'none' : 'transform 0.1s ease-out',
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// React.memoでラップして、chartプロップが変更されない限り再レンダリングを防ぐ
export const Mermaid = memo(MermaidComponent, (prevProps, nextProps) => {
  // chartプロップが同じ場合は再レンダリングをスキップ
  return prevProps.chart === nextProps.chart;
});

